import styles from './CustomMetricsCard.module.css';

/**
 * Glossary of FunViz's own proprietary metrics — the screener columns a
 * finance-literate reader could not predict from the name alone (our
 * composite scores, our consistency measures, and the handful of common
 * metrics where we made a deliberate definitional choice). Deliberately
 * excludes textbook metrics (EV/EBITDA, P/B, ROE, Altman Z, …), which make up
 * most of the screener's ~60 columns.
 *
 * Rendered as the body of a CollapsibleCard (see Screener.js), which already
 * supplies the card chrome, title and region semantics — so this component
 * has no outer <section>/<h2> of its own, just the labeled sub-sections
 * below.
 */
const CustomMetricsCard = () => (
  <>
    <p className={styles.intro}>
      Most columns in this screener are standard finance metrics. The ones
      below are FunViz&rsquo;s own — either scores we built, or common
      metrics where we made a deliberate definitional choice you couldn&rsquo;t
      guess from the name. Everything here is computed from annual reported
      figures.
    </p>

    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Our composite scores</h3>
      <dl className={styles.grid}>
        <div className={styles.entry}>
          <dt className={styles.term}>
            Quality (<code className={styles.key}>qualityScore</code>) — 0 to 100, higher is better.
          </dt>
          <dd className={styles.definition}>
            Built to pick out high-quality compounders: businesses that
            earn more year after year on steady margins, generate cash, do
            not dilute and are not over-indebted. Averaged over the last
            three reported fiscal years: profitability, share dilution,
            leverage, free-cash-flow quality, revenue quality and
            consistency, each capped so no single pillar can dominate{' '}
            <em>below the ceiling</em> — once the pillars sum past the
            maximum, further improvement in any one of them stops being
            visible. <strong>Consistency</strong> is weighted most, from
            &minus;40 to +40 points: earnings per share rising over the
            last five years, and stable operating margins over five years.
            Free-cash-flow quality (after stock compensation) adds up to 25,
            profitability and leverage up to 30 each, revenue growth up to
            15. Free-cash-flow and revenue quality are floored, so a single
            bad year contributes nothing rather than actively subtracting;
            that is what stops a cyclical business at a trough from reading
            as a failing one. Dilution and consistency are the only pillars
            that can subtract. Leverage is a <strong>free-cash-flow coverage</strong>{' '}
            measure with no price input at all — net debt against the cash
            the business actually generates — so the score no longer moves
            when the share price moves, a deliberate improvement on the
            earlier market-cap-based version. A bank&rsquo;s free cash flow
            isn&rsquo;t a coverage-meaningful number, so financials and REITs
            are measured on their own balance sheets instead: banks, insurers
            and other financials on <strong>equity to total assets</strong>{' '}
            (each against its own range — life insurers normally run far
            thinner equity than property insurers), except that goodwill-heavy
            or negative-equity financials (payment networks, broker roll-ups),
            whose book equity says little, stay on free-cash-flow coverage;
            REITs are measured on{' '}
            <strong>net debt to EBITDA</strong>, with EBITDA averaged over
            three years and stripped of one-off property-sale gains. The
            financial and REIT ranges are derived from live data and deliberately
            strict: a bank or insurer reaches full marks on a pillar at most
            about half as often as other companies do, and a REIT's leverage band
            and FCF-versus-earnings term are set on REIT-specific bases checked
            against live data. For banks and insurers,
            return on assets replaces net margin, net income replaces free
            cash flow and revenue, and there is no consistency pillar. Elsewhere, both surfaces average net margin
            with operating return on assets, so the same statement data gets
            the same arithmetic on either. Quality is
            blank, not zero, until three consecutive fiscal years score. It measures accounting stability and looks backward: for banks and insurers, the main risks (balance-sheet leverage, liquidity and tail events) are not fully visible in it.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            Growth (Total) and Growth (Per Share) (
            <code className={styles.key}>growthScoreTotal</code>,{' '}
            <code className={styles.key}>growthScorePerShare</code>) — 0 to 100.
          </dt>
          <dd className={styles.definition}>
            A take on the &ldquo;Rule of 40&rdquo;: each year, revenue
            growth plus three-quarters of that year&rsquo;s EBITDA margin
            (operating cash flow for REITs, whose EBITDA swings on property
            and securities gains), where a combined 70% or more scores 100
            and zero or less scores 0. Growth counts up to 100% a year and the
            margin between &minus;100% and 40%, so a very high margin
            can&rsquo;t carry the score on its own. Two versions: Per Share
            uses revenue per share, and Total uses the lower of total and
            per-share growth, so growth paid for with new shares does not
            count and buybacks lift only Per Share. Banks and other lenders
            (financials whose revenue is mostly interest income) are scored on
            revenue net of interest expense, with no margin credit and no
            forward estimate. Only growth to a new high
            counts: growth that recovers toward the revenue peak of the
            previous five years earns nothing, and growth counts less when
            revenue fell in more than 20% of up to the last ten years (nothing
            above 60%). Below 15% revenue growth the score is capped (5%
            &rarr; at most 33, 10% &rarr; at most 67, below or falling growth
            &rarr; 0), so
            a high margin can&rsquo;t make a slow grower look like a fast
            one. A year with zero or negative revenue, or growing from it,
            scores 0. The score shown is the average of the last
            three yearly scores (at most 30 after a year of zero or negative
            revenue), and is blank until three consecutive years score.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            Yield Score, Div Growth Score, EPS Growth Score (
            <code className={styles.key}>styleScoreYield</code>,{' '}
            <code className={styles.key}>styleScoreDivGrowth</code>,{' '}
            <code className={styles.key}>styleScoreEpsGrowth</code>) — 0 to 100.
          </dt>
          <dd className={styles.definition}>
            Fit to an investing <em>style</em>, not a verdict on the company.
            The same seven inputs are weighted three different ways: income,
            dividend growth, and earnings growth. A strong no-dividend
            compounder scores around 90 on EPS Growth and 0 on Yield — that
            spread is the information, and a single one of these numbers
            read alone is misleading. Three caveats worth stating: they are{' '}
            <strong>valuation-blind</strong> (the fair-value criterion that
            belongs in the formula isn&rsquo;t built yet, so an expensive
            company scores the same as a cheap one on identical
            fundamentals); they <strong>cannot rank bad companies</strong>{' '}
            (penalties push anything weak to a floor of 0, so
            &ldquo;mediocre&rdquo; and &ldquo;collapsing&rdquo; look
            identical — that floor is still a single absorbing point even
            though it&rsquo;s now drawn across a wider 0–100 axis, so a
            dense cluster of companies at 0 can imply finer resolution near
            the bottom than actually exists); and, on dividends
            specifically,{' '}
            <strong>
              a company confirmed to pay no dividend scores 0 on both Yield
              and Div Growth
            </strong>{' '}
            — correct, since there is no income or dividend growth to fit —
            while a company whose dividend history is simply{' '}
            <strong>unknown</strong> to us is deliberately left unpenalised,
            because &ldquo;we have no data&rdquo; is not the same fact as
            &ldquo;it pays nothing.&rdquo; EPS Growth Score ignores dividends
            entirely by design and is unaffected either way. One more honest
            note: there&rsquo;s a step at this boundary — paying nothing
            scores 0, while paying a trivial 0.3% yield can score around 45
            on Div Growth. The direction is right; the size of the step is a
            consequence of treating &ldquo;no dividend&rdquo; as categorical.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            The warning flag (<code className={styles.key}>styleScoreWarning</code>).
          </dt>
          <dd className={styles.definition}>
            A marker on the three score cells meaning &ldquo;scores well, but
            something just broke&rdquo;: the best of the three scores is
            still 60 or above, yet the price fell more than 30% in three
            months or analysts cut this year&rsquo;s earnings outlook by more
            than 20%. It is deliberately an <em>early</em> warning — once the
            damage reaches the 200-day trend the scores themselves drop below
            60 and the flag goes quiet, because by then the score is already
            telling you.
          </dd>
        </div>
      </dl>
    </section>

    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Consistency measures</h3>
      <dl className={styles.grid}>
        <div className={styles.entry}>
          <dt className={styles.term}>
            Earnings / Revenue / Cash Flow Stability (
            <code className={styles.key}>epsStab</code>,{' '}
            <code className={styles.key}>revStab</code>,{' '}
            <code className={styles.key}>cfStab</code>) — &minus;1 to +1.
          </dt>
          <dd className={styles.definition}>
            How straight the line is, not how steep: the correlation between
            the figure and time. A perfectly steady riser is 1.0, a steady
            decliner is &minus;1.0. Two things to know: a <em>cyclical but
            growing</em> business scores near 0.17 — almost as low as noise
            — because this measures trend-fit only; and a perfectly flat
            series is blank rather than 0, since a flat line has no trend to
            fit. Needs at least three years, and uses every annual year
            available.
          </dd>
        </div>
      </dl>
    </section>

    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Where we define it differently</h3>
      <dl className={styles.grid}>
        <div className={styles.entry}>
          <dt className={styles.term}>
            P/E (<code className={styles.key}>peBlended</code>).
          </dt>
          <dd className={styles.definition}>
            Price over an EPS figure interpolated between the last reported
            fiscal year and next year&rsquo;s analyst consensus, landing on
            today. Deliberately on a fiscal-year basis rather than
            trailing-twelve-month, because quarterly data isn&rsquo;t
            ingested here. Measured against the Analysis page&rsquo;s TTM
            version across 23 stocks: a median 3.6% difference, but up to
            about 26% when a one-off charge sits in the trailing quarters.
            Blank whenever the last fiscal year was a loss, even if earnings
            have since turned positive. The screener&rsquo;s own
            last-reported-fiscal-year figure (no interpolation) is labeled
            Trailing P/E instead.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            &ldquo;vs 5Y/10Y/15Y Avg&rdquo; columns (
            <code className={styles.key}>peVsAvg5y</code> and siblings across
            every valuation family — P/S, EV/EBITDA, EV/EBIT, P/FCF, P/OCF,
            Dividend Yield).
          </dt>
          <dd className={styles.definition}>
            What this stock has typically traded at over N years, and where
            it sits against that now, as a signed percent — e.g. -12% is
            12% cheaper than its own recent norm, +8% is 8% pricier. Built
            from N consecutive fiscal year-ends, skipping loss years and
            (for every family except Dividend Yield) implausibly extreme
            multiples, and reported as long as at least half the years are
            usable. So two stocks&rsquo; readings can rest on different
            amounts of evidence without the cell showing it. (This replaced
            a 5-year-median-based pair of columns on 2026-11-01 — nothing
            used median instead of average, so it was a redundant second
            stat answering the same question.)
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            Interest Coverage (<code className={styles.key}>interestCoverage</code>).
          </dt>
          <dd className={styles.definition}>
            Operating profit over interest expense, computed from the income
            statement rather than taken from our data provider&rsquo;s own
            ratio — that field reports a flat 0 for companies that don&rsquo;t
            break out interest expense, which reads as &ldquo;cannot cover
            its interest&rdquo; when the truth is &ldquo;has essentially
            none&rdquo;. Blank rather than 0 or infinite when there&rsquo;s no
            interest expense; can be negative when operating profit is.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            PEG (<code className={styles.key}>peg</code>) and Graham PEG
            (<code className={styles.key}>pegGraham</code>).
          </dt>
          <dd className={styles.definition}>
            Not the textbook PEG. It divides our own P/E by a growth
            rate that averages the last two years&rsquo; EPS growth and the
            next three years&rsquo; analyst-consensus EPS growth. Graham PEG
            divides by 8.5 plus twice that growth rate instead. Blank when
            growth is zero or negative or no three-year estimate exists.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            Shareholder Yield (<code className={styles.key}>shareholderYield</code>).
          </dt>
          <dd className={styles.definition}>
            Dividends plus net buybacks as a percentage of market cap. Note
            which version this is: it omits debt paydown, unlike the common
            three-factor definition, so it won&rsquo;t match figures from
            sources that include it. A net share <em>issuer</em> can read
            below its dividend yield. Blank, not zero, when dividend history
            is unknown — a confirmed non-payer reads a real 0.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>
            Payoff Years and repayment capacity (<code className={styles.key}>payoffYears</code>).
          </dt>
          <dd className={styles.definition}>
            How many years of spare cash flow it would take to clear net
            debt. The denominator prefers free cash flow but falls back to
            EBITDA when free cash flow is negative — our own rule, on the
            reasoning that a company can still service debt from EBITDA
            through a heavy-capex year. Consequence: the basis can switch
            between years for the same company, so consecutive readings
            aren&rsquo;t always on the same footing. This is not Net
            Debt/EBITDA, which is a separate column. Zero means net cash;
            blank means debt with no usable capacity.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>REIT earnings basis.</dt>
          {/* Field lists and formula pinned by __tests__/CustomMetricsCard.test.js
              against reit-ffo-golden.json (shared with funviz-amplify, ADR 0014). */}
          <dd className={styles.definition}>
            For REITs, Earnings Stability, EPS CAGR 5Y, EPS CAGR 10Y and
            Payout (EPS) use Funds From Operations per share instead of
            reported EPS, because depreciation on property swamps a
            REIT&rsquo;s reported EPS. P/E, Trailing P/E, Fwd P/E, PEG,
            Graham PEG and EPS CAGR (Est.) stay on reported EPS, because
            analyst forecasts exist only for EPS. The Analysis page&rsquo;s
            Earnings line is reported EPS too. Our FFO is net income plus
            depreciation and amortization, without the gains-on-property-sale
            adjustment the full definition includes, because that field
            isn&rsquo;t in the data we receive.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.term}>Special dividends.</dt>
          <dd className={styles.definition}>
            Before computing dividend growth, streak and stability, we drop
            payments larger than twice that company&rsquo;s median payment,
            so one bumper payout doesn&rsquo;t register as a permanent raise
            and then a cut the year after. Those one-offs are still counted
            in Dividend Yield and the payout ratios. A missed payment counts
            as a cut; a flat payment does not.
          </dd>
        </div>
      </dl>
    </section>

    <section className={styles.section}>
      <h3 className={styles.provenanceTitle}>Provenance</h3>
      <dl className={styles.provenanceGrid}>
        <div className={styles.entry}>
          <dt className={styles.provenanceTerm}>Why some cells are blank.</dt>
          <dd className={styles.provenanceDefinition}>
            A figure named for a window is blank rather than approximated
            when that much history isn&rsquo;t there — a 10-year growth rate
            on a company with six years of data reads blank, never a
            six-year number wearing a 10-year label. Throughout the
            screener, blank means &ldquo;unknown&rdquo; and is never silently
            treated as zero.
          </dd>
        </div>

        <div className={styles.entry}>
          <dt className={styles.provenanceTerm}>Basis stamps.</dt>
          <dd className={styles.provenanceDefinition}>
            Every row records which basis produced its numbers: whether a
            currency conversion was applied, whether earnings came from EPS
            or FFO, and which weighting produced the style scores. These
            aren&rsquo;t shown as columns; they exist so a number can always
            be traced.
          </dd>
        </div>
      </dl>
    </section>
  </>
);

export default CustomMetricsCard;
